export type FieldType = "text" | "date" | "currency" | "number" | "tel" | "email" | "yesno" | "select"

export interface FieldDef {
  key: string
  label: string
  type: FieldType
  standard?: string
  customKey?: string
  options?: string[]   // for type="select"
  // Layout: span = columns out of 12. Default: half→6, no half→12.
  span?: number       // 1-12 columns
  half?: boolean      // legacy shorthand for span:6 (used by policy fields)
  rowStart?: boolean  // force grid-column to start at col 1 (ensures new row)
  isGender?: boolean        // rendered in card header, excluded from field grid
  isPreferredName?: boolean // rendered inline in card header, excluded from field grid
}

export interface SectionDef {
  id: string
  label: string
  fields: FieldDef[]
}

// ---------------------------------------------------------------------------
// PRIMARY APPLICANT
// Row 1: first(3) + last(3) + dob(6)
// Row 2: phone(6) + email(6)
// Row 3: height(6) + weight(6)
// Row 4: annualIncome(6) + householdSize(6)
// Row 5: tobacco(6, rowStart) + medicaidId(6)
// Row 6: medicareId(6, rowStart) + partA(3) + partB(3)
// ---------------------------------------------------------------------------
const primaryFields: FieldDef[] = [
  { key: "primaryGender",  label: "Gender",                         type: "text",     customKey: "contact.gender",                           isGender: true },
  { key: "preferredName",  label: "Preferred Name",                 type: "text",     customKey: "contact.preferred_name",                   isPreferredName: true },
  { key: "firstName",      label: "First Name",                     type: "text",     standard:  "firstName",                                span: 3 },
  { key: "lastName",       label: "Last Name",                      type: "text",     standard:  "lastName",                                 span: 3 },
  { key: "dateOfBirth",    label: "Date of Birth",                  type: "date",     standard:  "dateOfBirth",                              span: 6 },
  { key: "phone",          label: "Phone",                          type: "tel",      standard:  "phone",                                    span: 6 },
  { key: "email",          label: "Email",                          type: "email",    standard:  "email",                                    span: 6 },
  { key: "annualIncome",   label: "Annual Household Income (MAGI)", type: "currency", customKey: "contact.magi",                             span: 6 },
  { key: "householdSize",  label: "Household Size",                 type: "number",   customKey: "contact.house_hold_size",                  span: 6 },
  { key: "height",         label: "Height",                         type: "text",     customKey: "contact.what_is_your_height_and_weight",   span: 6 },
  { key: "weight",         label: "Weight (lbs)",                   type: "number",   customKey: "contact.weight",                           span: 6 },
  { key: "primaryTobacco", label: "Tobacco",                        type: "yesno",    customKey: "contact.are_you_a_tobacco_smoker",         span: 6, rowStart: true },
  { key: "medicaidId",     label: "Medicaid ID",                    type: "text",     customKey: "contact.medicaid_id",                      span: 6 },
  { key: "medicareId",     label: "Medicare ID (MBI)",              type: "text",     customKey: "contact.mbi",                              span: 6, rowStart: true },
  { key: "partAEffDate",   label: "Part A Effective Date",          type: "date",     customKey: "contact.part_a_effective_date",            span: 3 },
  { key: "partBEffDate",   label: "Part B Effective Date",          type: "date",     customKey: "contact.part_b_effective_date",            span: 3 },
]

// ---------------------------------------------------------------------------
// ADDRESS (separate card)
// Row 1: address(6) + city(2) + state(2) + zip(2)
// Row 2: county(6) + timezone(6)
// ---------------------------------------------------------------------------
const addressFields: FieldDef[] = [
  { key: "address1",   label: "Address",   type: "text", standard:  "address1",           span: 6 },
  { key: "county",     label: "County",    type: "text", customKey: "contact.county",     span: 6 },
  { key: "city",       label: "City",      type: "text", standard:  "city",               span: 2 },
  { key: "state",      label: "State",     type: "text", standard:  "state",              span: 2 },
  { key: "postalCode", label: "Zip",       type: "text", standard:  "postalCode",         span: 2 },
  { key: "timezone",   label: "Time Zone", type: "text", standard:  "timezone",            span: 6 },
]

// ---------------------------------------------------------------------------
// SPOUSE
// Row 1: first(3) + last(3) + dob(6)
// Row 2: phone(6) + email(6)
// Row 3: height(6) + weight(6)
// Row 4: tobacco(6) + relationship(6)
// ---------------------------------------------------------------------------
const spouseFields: FieldDef[] = [
  { key: "spouseGender",       label: "Gender",        type: "text",   customKey: "contact.spouse_gender",              isGender: true },
  { key: "spouseFirst",        label: "First Name",    type: "text",   customKey: "contact.clients_spouse",                     span: 3 },
  { key: "spouseLast",         label: "Last Name",     type: "text",   customKey: "contact.contactclients_spouse_lastname",     span: 3 },
  { key: "spouseDob",          label: "Date of Birth", type: "date",   customKey: "contact.spouse_date_of_birth",               span: 6 },
  { key: "spousePhone",        label: "Phone",         type: "tel",    customKey: "contact.spouse_phone",               span: 6 },
  { key: "spouseEmail",        label: "Email",         type: "email",  customKey: "contact.spouse_email",               span: 6 },
  { key: "spouseHeight",       label: "Height",        type: "text",   customKey: "contact.spouse_height",              span: 6 },
  { key: "spouseWeight",       label: "Weight (lbs)",  type: "number", customKey: "contact.spouse_weight",              span: 6 },
  { key: "spouseTobacco",      label: "Tobacco",       type: "yesno",  customKey: "contact.spouse_a_tobacco_smoker",   span: 6 },
  { key: "spouseRelationship", label: "Relationship",  type: "text",   customKey: "contact.spouse_relationship",        span: 6 },
]

// ---------------------------------------------------------------------------
// DEPENDENTS 1–6
// Row 1: first(3) + last(3) + dob(6)
// Row 2: height(6) + weight(6)
// Row 3: tobacco(6) + relationship(6)
// ---------------------------------------------------------------------------
function makeDepFields(n: number): FieldDef[] {
  // GHL uses contact.dependent_N (no suffix) for first name
  // Dep 1 last name has an inconsistent key: contactdependent_1_lastname
  const firstKey = `contact.dependent_${n}`
  const lastKey  = n === 1 ? `contact.contactdependent_1_lastname` : `contact.dependent_${n}_lastname`
  return [
    { key: `dep${n}Gender`,       label: "Gender",       type: "text",   customKey: `contact.dependent_${n}_gender`,           isGender: true },
    { key: `dep${n}First`,        label: "First",        type: "text",   customKey: firstKey,                                  span: 3 },
    { key: `dep${n}Last`,         label: "Last",         type: "text",   customKey: lastKey,                                   span: 3 },
    { key: `dep${n}Dob`,          label: "DOB",          type: "date",   customKey: `contact.dependent_${n}_dob`,              span: 6 },
    { key: `dep${n}Height`,       label: "Height",       type: "text",   customKey: `contact.dependent_${n}_height`,           span: 6 },
    { key: `dep${n}Weight`,       label: "Weight (lbs)", type: "number", customKey: `contact.dependent_${n}_weight`,           span: 6 },
    { key: `dep${n}Tobacco`,      label: "Tobacco",      type: "yesno",  customKey: `contact.dependent_${n}_a_tobacco_smoker`, span: 6 },
    { key: `dep${n}Relationship`, label: "Relationship", type: "text",   customKey: `contact.dependent_${n}_relationship`,     span: 6 },
  ]
}

// ---------------------------------------------------------------------------
// ACTIVE POLICIES 1–8
// Row 1: Carrier (6) | Product (6)
// Row 2: Policy # (6) | Line of Business (6)
// Row 3: Premium (6) | Application Date (3) | Effective Date (3)
// ---------------------------------------------------------------------------
function makePolicyFields(n: number): FieldDef[] {
  return [
    { key: `p${n}Carrier`,        label: "Carrier",          type: "text",     customKey: `contact.policy_${n}_carrier`,          span: 6 },
    { key: `p${n}Product`,        label: "Product",          type: "text",     customKey: `contact.policy_${n}_product_name`,     span: 6 },
    { key: `p${n}Number`,         label: "Policy #",         type: "text",     customKey: `contact.policy_${n}_number`,           span: 6 },
    { key: `p${n}LineOfBusiness`, label: "Line of Business", type: "text",     customKey: `contact.policy_${n}_line_of_business`, span: 6 },
    { key: `p${n}Premium`,        label: "Premium ($)",      type: "currency", customKey: `contact.policy_${n}_monthly_premium`,  span: 6 },
    { key: `p${n}AppDate`,        label: "Application Date", type: "date",     customKey: `contact.policy_${n}_application_date`, span: 3 },
    { key: `p${n}EffDate`,        label: "Effective Date",   type: "date",     customKey: `contact.policy_${n}_effective_date`,   span: 3 },
  ]
}

// ---------------------------------------------------------------------------
// PAST POLICIES 1–8
// ---------------------------------------------------------------------------
function makePastPolicyFields(n: number): FieldDef[] {
  return [
    { key: `pp${n}CarrierProduct`, label: "Carrier + Product",             type: "text", customKey: `contact.past_policy_${n}_carrier__product`,                   half: true },
    { key: `pp${n}LineOfBusiness`, label: "Line of Business",              type: "text", customKey: `contact.past_policy_${n}_line_of_business`,                   half: true },
    { key: `pp${n}EffCancelDate`,  label: "Effective / Cancellation Date", type: "text", customKey: `contact.past_policy_${n}_effective_date_cancellation_date`,    half: true },
    { key: `pp${n}ArchivedDate`,   label: "Archived Date",                 type: "date", customKey: `contact.past_policy_${n}_archived_date`,                      half: true },
  ]
}

// ---------------------------------------------------------------------------
// DOCTORS 1–5  (Preferred Hospital is a single accordion-level field)
// Row 1: Name (12)
// Row 2: Specialty (12)
// Row 3: Address (12)
// ---------------------------------------------------------------------------
function makeDoctorFields(n: number): FieldDef[] {
  return [
    { key: `doc${n}Name`,     label: "Name",      type: "text", customKey: `contact.doctor_${n}_name`,      span: 12 },
    { key: `doc${n}Specialty`, label: "Specialty", type: "text", customKey: `contact.doctor_${n}_specialty`, span: 12 },
    { key: `doc${n}Address`,  label: "Address",   type: "text", customKey: `contact.doctor_${n}_address`,   span: 12 },
  ]
}

// ---------------------------------------------------------------------------
// MEDICATIONS 1–10  (Preferred Pharmacy is a single accordion-level field)
// GHL key pattern: rx_N_name / rx_N_dosage / rx_N_quantity
// Frequency key naming is inconsistent in GHL: rx1-5 use frequency_rxN,
// rx6-10 use frequency_rx_N (underscore before number).
// Row 1: Name (6) | Dosage (6)
// Row 2: Quantity (6) | Frequency (6)
// ---------------------------------------------------------------------------
function makeMedicationFields(n: number): FieldDef[] {
  // Rx #1 frequency key is bare "frequency"; #2 has no underscore; #3-10 have underscore
  const freqKey = n === 1 ? "contact.frequency"
                : n === 2 ? "contact.frequency_rx2"
                : `contact.frequency_rx_${n}`
  return [
    { key: `med${n}Name`,      label: "Name",      type: "text", customKey: `contact.rx_${n}_name`,      span: 6 },
    { key: `med${n}Dosage`,    label: "Dosage",    type: "text", customKey: `contact.rx_${n}_dosage`,    span: 6 },
    { key: `med${n}Quantity`,  label: "Quantity",  type: "text", customKey: `contact.rx_${n}_quantity`,  span: 6 },
    { key: `med${n}Frequency`, label: "Frequency", type: "select", customKey: freqKey, span: 6,
      options: ["Every Month", "Every 2 Months", "Every 3 Months", "Every 6 Months", "Every 12 Months"] },
  ]
}

// ---------------------------------------------------------------------------
// Singleton accordion-level fields (not part of any card)
// ---------------------------------------------------------------------------
export const PREFERRED_HOSPITAL_FIELD: FieldDef = {
  key: "preferredHospital",
  label: "🏥 Preferred Hospital(s)",
  type: "text",
  customKey: "contact.indicate_your_preferred_hospitals",
  span: 12,
}

export const PREFERRED_PHARMACY_FIELD: FieldDef = {
  key: "preferredPharmacy",
  label: "💊 Preferred Pharmacy",
  type: "text",
  customKey: "contact.preferred_pharmacy",
  span: 12,
}

// ---------------------------------------------------------------------------
// All sections in display order
// ---------------------------------------------------------------------------
export const SECTIONS: SectionDef[] = [
  { id: "primary",     label: "Primary Applicant", fields: primaryFields        },
  { id: "address",     label: "Address",           fields: addressFields        },
  { id: "spouse",      label: "Spouse",            fields: spouseFields         },
  { id: "dep1",        label: "Dependent 1",       fields: makeDepFields(1)     },
  { id: "dep2",        label: "Dependent 2",       fields: makeDepFields(2)     },
  { id: "dep3",        label: "Dependent 3",       fields: makeDepFields(3)     },
  { id: "dep4",        label: "Dependent 4",       fields: makeDepFields(4)     },
  { id: "dep5",        label: "Dependent 5",       fields: makeDepFields(5)     },
  { id: "dep6",        label: "Dependent 6",       fields: makeDepFields(6)     },
  { id: "policy1",     label: "Policy 1",          fields: makePolicyFields(1)     },
  { id: "policy2",     label: "Policy 2",          fields: makePolicyFields(2)     },
  { id: "policy3",     label: "Policy 3",          fields: makePolicyFields(3)     },
  { id: "policy4",     label: "Policy 4",          fields: makePolicyFields(4)     },
  { id: "policy5",     label: "Policy 5",          fields: makePolicyFields(5)     },
  { id: "policy6",     label: "Policy 6",          fields: makePolicyFields(6)     },
  { id: "policy7",     label: "Policy 7",          fields: makePolicyFields(7)     },
  { id: "policy8",     label: "Policy 8",          fields: makePolicyFields(8)     },
  { id: "doc1",        label: "Doctor #1",         fields: makeDoctorFields(1)     },
  { id: "doc2",        label: "Doctor #2",         fields: makeDoctorFields(2)     },
  { id: "doc3",        label: "Doctor #3",         fields: makeDoctorFields(3)     },
  { id: "doc4",        label: "Doctor #4",         fields: makeDoctorFields(4)     },
  { id: "doc5",        label: "Doctor #5",         fields: makeDoctorFields(5)     },
  { id: "med1",        label: "Rx #1",             fields: makeMedicationFields(1)  },
  { id: "med2",        label: "Rx #2",             fields: makeMedicationFields(2)  },
  { id: "med3",        label: "Rx #3",             fields: makeMedicationFields(3)  },
  { id: "med4",        label: "Rx #4",             fields: makeMedicationFields(4)  },
  { id: "med5",        label: "Rx #5",             fields: makeMedicationFields(5)  },
  { id: "med6",        label: "Rx #6",             fields: makeMedicationFields(6)  },
  { id: "med7",        label: "Rx #7",             fields: makeMedicationFields(7)  },
  { id: "med8",        label: "Rx #8",             fields: makeMedicationFields(8)  },
  { id: "med9",        label: "Rx #9",             fields: makeMedicationFields(9)  },
  { id: "med10",       label: "Rx #10",            fields: makeMedicationFields(10) },
  { id: "pastPolicy1", label: "Past Policy 1",     fields: makePastPolicyFields(1) },
  { id: "pastPolicy2", label: "Past Policy 2",     fields: makePastPolicyFields(2) },
  { id: "pastPolicy3", label: "Past Policy 3",     fields: makePastPolicyFields(3) },
  { id: "pastPolicy4", label: "Past Policy 4",     fields: makePastPolicyFields(4) },
  { id: "pastPolicy5", label: "Past Policy 5",     fields: makePastPolicyFields(5) },
  { id: "pastPolicy6", label: "Past Policy 6",     fields: makePastPolicyFields(6) },
  { id: "pastPolicy7", label: "Past Policy 7",     fields: makePastPolicyFields(7) },
  { id: "pastPolicy8", label: "Past Policy 8",     fields: makePastPolicyFields(8) },
]

// ALL_FIELDS includes section fields + the two singleton accordion-level fields
export const ALL_FIELDS: FieldDef[] = [
  ...SECTIONS.flatMap(s => s.fields),
  PREFERRED_HOSPITAL_FIELD,
  PREFERRED_PHARMACY_FIELD,
]

// ---------------------------------------------------------------------------
// Helpers for reading GHL contact data into form state
// ---------------------------------------------------------------------------
export function buildCustomIndex(customFields: any[]): Record<string, string> {
  const index: Record<string, string> = {}
  if (!Array.isArray(customFields)) return index
  for (const cf of customFields) {
    if (cf.fieldKey) index[cf.fieldKey] = cf.value ?? ""
  }
  return index
}

export function getContactValue(
  field: FieldDef,
  contact: any,
  customIndex: Record<string, string>
): string {
  if (field.standard)  return String(contact[field.standard]      ?? "")
  if (field.customKey) return String(customIndex[field.customKey] ?? "")
  return ""
}

export function buildFormState(contact: any): Record<string, string> {
  const customIndex = buildCustomIndex(contact?.customFields ?? [])
  const state: Record<string, string> = {}
  for (const field of ALL_FIELDS) {
    state[field.key] = getContactValue(field, contact, customIndex)
  }
  // Default frequency to "Every Month" when quantity is filled but frequency is empty
  for (let n = 1; n <= 10; n++) {
    if (state[`med${n}Quantity`] && !state[`med${n}Frequency`]) {
      state[`med${n}Frequency`] = "Every Month"
    }
  }
  return state
}
